/**
 * SEO-friendly URL slug helpers — JS twin of `/app/backend/utils/slugify.py`.
 *
 * Used wherever the SPA generates a share/public link so the URL is readable
 * in messengers and indexable by Google. Greek and Latin diacritics are
 * transliterated to plain ASCII.
 *
 * IMPORTANT — backwards compatibility:
 * The URL pattern stays `/{kind}/{slug}--{uuid}`. The slug is cosmetic — every
 * consumer (backend OG routes, SPA routes) strips the `--<uuid>` tail and looks
 * up by uuid only. That's why we can change the slug format without breaking
 * any of the user's already-shared links.
 */

// Multi-letter digraphs FIRST so "ου" → "ou" wins over "ο"+"υ" → "oy".
const GREEK_DIGRAPHS = [
  ["ου", "ou"], ["αυ", "av"], ["ευ", "ev"], ["ηυ", "iv"],
  ["γγ", "ng"], ["γκ", "g"], ["μπ", "b"], ["ντ", "d"],
  ["τσ", "ts"], ["τζ", "tz"],
];

const GREEK_MAP = {
  α: "a", β: "v", γ: "g", δ: "d", ε: "e", ζ: "z", η: "i",
  θ: "th", ι: "i", κ: "k", λ: "l", μ: "m", ν: "n", ξ: "x",
  ο: "o", π: "p", ρ: "r", σ: "s", ς: "s", τ: "t", υ: "y",
  φ: "f", χ: "ch", ψ: "ps", ω: "o",
};

export function slugify(name, maxLen = 80) {
  if (!name) return "";
  // Lower + NFD strip diacritics
  let s = name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // Digraphs first
  for (const [src, dst] of GREEK_DIGRAPHS) s = s.split(src).join(dst);
  // Single-letter map
  s = s.replace(/./g, ch => GREEK_MAP[ch] ?? ch);
  // Keep [a-z0-9], everything else becomes a dash, collapse + trim
  const out = [];
  for (const ch of s) {
    if ((ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9")) out.push(ch);
    else if (out.length && out[out.length - 1] !== "-") out.push("-");
  }
  let slug = out.join("").replace(/^-+|-+$/g, "");
  if (slug.length > maxLen) slug = slug.slice(0, maxLen).replace(/-+$/, "");
  return slug;
}

/**
 * Build a `{slug}--{uuid}` path segment from an entity (name + id).
 * If `name` is empty, returns the uuid alone (still a valid URL).
 */
export function entitySlug(name, id) {
  const s = slugify(name || "");
  return s ? `${s}--${id}` : String(id);
}

/**
 * Build a full share URL using the OG endpoint (rich crawler card) for the
 * given kind/id. The slug is cosmetic — backend strips it. Used in share
 * buttons across the app so messengers scrape rich previews instead of the
 * SPA shell.
 */
export function shareOgUrl(kind, id, name) {
  if (typeof window === "undefined") return "";
  const slug = entitySlug(name || "", id);
  return `${window.location.origin}/api/og/${kind}/${slug}`;
}
